import { QrCode } from "lucide-react";
import { Link } from "react-router-dom";
import { CopyableAddress } from "@/components/copyable-address";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardAction,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Hint } from "@/components/ui/tooltip";
import { intakeAddress } from "@/lib/intake-address";
import { formatPublicCode } from "@/lib/public-code";
import type { SiteEntrance } from "./site-setup-types";

type SiteEntrancesSectionProps = {
  siteId: string;
  siteName?: string | null;
  entrances: SiteEntrance[];
};

export function SiteEntrancesSection({
  siteId,
  siteName,
  entrances,
}: SiteEntrancesSectionProps) {
  return (
    <Card className="gap-0 overflow-hidden py-0">
      <CardHeader className="px-6 pt-6 pb-4">
        <CardTitle>Entrances</CardTitle>
        <CardDescription>
          {siteName
            ? `Access points for ${siteName}.`
            : "Access points for this site."}
        </CardDescription>
        <CardAction>
          <Button asChild variant="outline" size="sm">
            <Link to={`/sites/${siteId}/entrances/new`}>Add entrance</Link>
          </Button>
        </CardAction>
      </CardHeader>

      {entrances.length === 0 ? (
        <div className="border-t border-border px-6 py-8 text-sm text-muted-foreground">
          No entrances configured yet.
        </div>
      ) : (
        <div className="overflow-x-auto border-t border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="pl-6">Entrance</TableHead>
                <TableHead className="w-10">QR</TableHead>
                <TableHead>Code</TableHead>
                <TableHead>Location</TableHead>
                <TableHead className="text-right">Geofence</TableHead>
                <TableHead className="pr-6">Email</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entrances.map((entrance) => (
                <TableRow key={entrance.id}>
                  <TableCell className="pl-6">
                    <div className="flex items-center gap-2">
                      <Link
                        to={`/sites/${siteId}/entrances/${entrance.id}/edit`}
                        className="font-medium text-foreground hover:underline"
                      >
                        {entrance.name}
                      </Link>
                      {entrance.isPrimary ? (
                        <Badge variant="outline" size="sm">
                          Primary
                        </Badge>
                      ) : null}
                    </div>
                  </TableCell>
                  <TableCell className="w-10">
                    <Hint content={`Open QR code for ${entrance.name}`}>
                      <Button
                        asChild
                        variant="ghost"
                        size="icon-xs"
                        className="-my-1"
                      >
                        <a
                          href={`/sites/${siteId}/qr?entrance=${entrance.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={`Open QR code for ${entrance.name}`}
                        >
                          <QrCode aria-hidden="true" />
                        </a>
                      </Button>
                    </Hint>
                  </TableCell>
                  <TableCell className="font-meta text-muted-foreground">
                    {formatPublicCode(entrance.publicCode) ?? "Code pending"}
                  </TableCell>
                  <TableCell className="font-meta text-muted-foreground">
                    {entrance.plusCode ||
                      `${entrance.lat.toFixed(6)}, ${entrance.lon.toFixed(6)}`}
                  </TableCell>
                  <TableCell className="text-right font-meta text-muted-foreground">
                    {entrance.geofenceDistance}m
                  </TableCell>
                  <TableCell className="whitespace-normal pr-6">
                    <CopyableAddress
                      compact
                      label={`${entrance.name} email`}
                      tooltip={`Copy ${entrance.name} email`}
                      address={intakeAddress(entrance.publicCode)}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </Card>
  );
}
